import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { WhatsAppSocketProvider, useWhatsAppSocket } from '../../context/WhatsAppSocketContext';
import { 
  Send, Check, CheckCheck, ShieldAlert, Phone, 
  User as UserIcon, MessageCircle, UserCheck, 
  History, Clock, Plus, FileText, Paperclip
} from 'lucide-react';
import { MediaSendButton, MediaPreview, LibrarySendButton } from '../../components/whatsapp/MediaSendButton';
import NewChatModal from '../../components/whatsapp/NewChatModal';
import QuickReplies from '../../components/whatsapp/QuickReplies';
import MediaPickerModal from '../../components/shared/MediaPickerModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WhatsAppPageContent = () => {
  const { user } = useAuth();
  const { socket, isConnected: socketConnected } = useWhatsAppSocket();
  
  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [logs, setLogs] = useState([]);
  
  const [activeTab, setActiveTab] = useState('unassigned'); // 'unassigned' | 'mine'
  const [newMessageText, setNewMessageText] = useState('');
  
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatInitialPhone, setNewChatInitialPhone] = useState('');
  
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  const messagesEndRef = useRef(null);

  // 1. Fetch Config
  const fetchConfig = async () => {
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        if (data.isConnected) {
          fetchContacts();
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  // 2. Fetch Contacts based on activeTab
  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const token = localStorage.getItem('crm_token');
      let endpoint = '/whatsapp/contacts/unassigned';
      if (activeTab === 'mine') endpoint = '/whatsapp/contacts/mine';

      const res = await fetch(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // 3. Fetch Conversation messages
  const fetchMessages = async (phone) => {
    try {
      setLoadingMessages(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/messages/${phone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        // Zero out unread count in UI contact list instantly
        setContacts(prev =>
          prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
        );
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // 4. Fetch Activity Logs
  const fetchLogs = async (contactId) => {
    try {
      setLoadingLogs(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 5. Accept unassigned chat
  const handleAcceptChat = async (contactId) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        fetchContacts();
        if (activeContact && activeContact._id === contactId) {
          setActiveContact(data.contact);
          fetchLogs(contactId);
        }
      }
    } catch (err) {
      console.error('Error accepting chat:', err);
    }
  };

  // 6. Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessageText.trim() && !selectedMedia) || !activeContact || sendingMessage) return;

    // Executive can only reply if assigned to them
    if (activeContact.assignedTo !== user.id) {
      alert('You can only reply to chats assigned to you. Please accept this chat first.');
      return;
    }

    const to = activeContact.phone;
    const body = newMessageText.trim();

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('crm_token');
      let res;

      if (selectedMedia) {
        const formData = new FormData();
        formData.append('to', to);
        if (body) formData.append('caption', body);
        
        if (selectedMedia.fileUrl) {
          formData.append('mediaUrl', selectedMedia.fileUrl);
        } else {
          formData.append('file', selectedMedia);
        }

        res = await fetch(`${API_BASE}/whatsapp/send-media`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
      } else {
        res = await fetch(`${API_BASE}/whatsapp/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ to, message: body })
        });
      }

      if (res.ok) {
        setNewMessageText('');
        setSelectedMedia(null);
        if (activeContact) {
          fetchLogs(activeContact._id);
        }
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to send message via Meta Cloud API.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Auto Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial load
  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch contacts when tab changes
  useEffect(() => {
    fetchContacts();
  }, [activeTab]);

  // Fetch thread when contact switches
  useEffect(() => {
    if (activeContact) {
      fetchMessages(activeContact.phone);
      fetchLogs(activeContact._id);
    }
  }, [activeContact]);

  // Real-time socket event subscription
  useEffect(() => {
    if (!socket) return;

    const handleNewMsg = ({ contact, message }) => {
      if (activeContact && activeContact.phone === message.contactPhone) {
        setMessages(prev => {
          if (message.wamid && prev.some(m => m.wamid === message.wamid)) return prev;
          return [...prev, message];
        });
        fetch(`${API_BASE}/whatsapp/messages/${activeContact.phone}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
        });
        fetchLogs(activeContact._id);
      }
      fetchContacts();
    };

    const handleStatusUpdate = ({ contactPhone, wamid, status }) => {
      if (activeContact && activeContact.phone === contactPhone) {
        setMessages(prev =>
          prev.map(m => (m.wamid === wamid ? { ...m, status } : m))
        );
      }
    };

    const handleRead = ({ phone }) => {
      setContacts(prev =>
        prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
      );
    };

    const handleChatAssignedEvent = () => {
      fetchContacts();
      if (activeContact) {
        fetchLogs(activeContact._id);
      }
    };

    const handleChatDeletedEvent = ({ contactId }) => {
      if (activeContact && activeContact._id === contactId) {
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
      }
      fetchContacts();
    };

    socket.on('new_message', handleNewMsg);
    socket.on('message_status_update', handleStatusUpdate);
    socket.on('contact_read', handleRead);
    socket.on('chat_assigned', handleChatAssignedEvent);
    socket.on('chat_auto_assigned', handleChatAssignedEvent);
    socket.on('chat_reassigned', handleChatAssignedEvent);
    socket.on('chat_deleted', handleChatDeletedEvent);

    return () => {
      socket.off('new_message', handleNewMsg);
      socket.off('message_status_update', handleStatusUpdate);
      socket.off('contact_read', handleRead);
      socket.off('chat_assigned', handleChatAssignedEvent);
      socket.off('chat_auto_assigned', handleChatAssignedEvent);
      socket.off('chat_reassigned', handleChatAssignedEvent);
      socket.off('chat_deleted', handleChatDeletedEvent);
    };
  }, [socket, activeContact]);

  // Helper render status ticks
  const renderMsgStatus = (status) => {
    switch (status) {
      case 'read':
        return <CheckCheck size={16} color="#34B7F1" />;
      case 'delivered':
        return <CheckCheck size={16} color="var(--color-text-tertiary)" />;
      case 'sent':
        return <Check size={16} color="var(--color-text-tertiary)" />;
      case 'failed':
        return <ShieldAlert size={16} color="#E24B4A" title="Failed to deliver" />;
      default:
        return null;
    }
  };

  // Helper to format actions in activity timeline logs
  const renderLogActionIcon = (action) => {
    switch (action) {
      case 'chat_received':
        return <MessageCircle size={16} color="#10b981" />;
      case 'auto_assigned':
        return <UserCheck size={16} color="#3b82f6" />;
      case 'manually_accepted':
        return <Check size={16} color="#8b5cf6" />;
      case 'reassigned':
        return <Clock size={16} color="#f59e0b" />;
      case 'message_sent':
        return <Send size={14} color="#64748b" />;
      case 'message_received':
        return <Clock size={14} color="#10b981" />;
      default:
        return <History size={16} color="#64748b" />;
    }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', background: '#f5f7fb' }}>
      
      {/* ── LEFT PANEL: TAB SWITCHER & CONTACTS ────────────────────────────────── */}
      <div style={{
        width: '340px',
        background: 'white',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header Section */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>WhatsApp Inbox</h3>
            {config?.isConnected && (
              <span style={{ fontSize: '12px', color: socketConnected ? '#22c55e' : '#f59e0b', fontWeight: '500' }}>
                ● {socketConnected ? 'Live Connection' : 'Connecting...'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {config?.isConnected && (
              <button 
                onClick={() => {
                  setNewChatInitialPhone('');
                  setShowNewChatModal(true);
                }}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#16a34a'}
                onMouseLeave={e => e.currentTarget.style.background = '#22c55e'}
              >
                <Plus size={14} /> New Chat
              </button>
            )}
          </div>
        </div>

        {/* Custom Tab Bar */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          padding: '4px'
        }}>
          {['unassigned', 'mine'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActiveContact(null);
              }}
              style={{
                flex: 1,
                padding: '8px 4px',
                fontSize: '13px',
                fontWeight: activeTab === tab ? '600' : '500',
                color: activeTab === tab ? '#22c55e' : '#64748b',
                background: activeTab === tab ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              {tab === 'unassigned' && 'Unassigned'}
              {tab === 'mine' && 'My Chats'}
            </button>
          ))}
        </div>

        {/* Contacts Scroller */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingContacts && contacts.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading conversations...</div>
          ) : contacts.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
              <MessageCircle size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <div>No chats in this queue</div>
              <div style={{ fontSize: '11px', marginTop: '6px' }}>Incoming WhatsApp messages will appear here.</div>
            </div>
          ) : (
            contacts.map((c) => {
              const isActive = activeContact && activeContact.phone === c.phone;
              return (
                <div
                  key={c._id || c.phone}
                  onClick={() => setActiveContact(c)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: isActive ? '#f0fdf4' : 'transparent',
                    borderBottom: '1px solid #f1f5f9',
                    borderLeft: isActive ? '3px solid #22c55e' : '3px solid transparent',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#e2fbe8',
                      color: '#15803d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      marginRight: '12px'
                    }}>
                      {c.name ? c.name[0].toUpperCase() : <UserIcon size={16} />}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#334155', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {c.name || `+${c.phone}`}
                        </span>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                          {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '150px' }}>
                          {c.lastMessagePreview || 'Click to view chat'}
                        </span>
                        {c.unreadCount > 0 && (
                          <div style={{
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '12px',
                            minWidth: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            padding: '0 6px'
                          }}>
                            {c.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {activeTab === 'unassigned' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptChat(c._id);
                        }}
                        style={{
                          background: '#22c55e',
                          color: 'white',
                          border: 'none',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginLeft: '52px'
                        }}
                      >
                        Accept Chat
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── CENTER PANEL: CHAT WINDOW ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#eae6df' }}>
        {activeContact ? (
          <>
            {/* Chat header */}
            <div style={{
              padding: '12px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#e2fbe8',
                  color: '#15803d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginRight: '12px'
                }}>
                  {activeContact.name ? activeContact.name[0].toUpperCase() : <UserIcon size={16} />}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>{activeContact.name || 'WhatsApp Contact'}</h4>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>+{activeContact.phone}</span>
                </div>
              </div>

              {/* Assignment Actions */}
              <div>
                {(!activeContact.assignedTo || activeContact.status === 'unassigned') ? (
                  <button
                    onClick={() => handleAcceptChat(activeContact._id)}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <UserCheck size={16} /> Accept Chat
                  </button>
                ) : (
                  <span style={{
                    fontSize: '12px',
                    color: '#64748b',
                    background: '#f1f5f9',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontWeight: '500'
                  }}>
                    👤 Assigned to: {activeContact.assignedToName} {activeContact.assignedTo === user.id ? '(You)' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Messages box */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              backgroundImage: 'radial-gradient(#dfdcd6 10%, transparent 10%)',
              backgroundSize: '20px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {loadingMessages && messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', background: '#ffffffcc', padding: '8px 16px', borderRadius: '20px', margin: 'auto' }}>
                  Loading chat...
                </div>
              ) : (
                messages.map((m) => {
                  const isOut = m.direction === 'outbound';
                  return (
                    <div
                      key={m._id || m.wamid}
                      style={{
                        alignSelf: isOut ? 'flex-end' : 'flex-start',
                        background: isOut ? (m.sentByName === 'AI Assistant' ? '#f3e8ff' : '#d9fdd3') : 'white',
                        color: '#303030',
                        padding: '8px 12px 6px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                        maxWidth: '60%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        wordBreak: 'break-word'
                      }}
                    >
                      {m.mediaUrl && (
                        ['image', 'video'].includes(m.type) ? (
                          <img 
                            src={m.mediaUrl.startsWith('http') ? m.mediaUrl : `http://localhost:5000${m.mediaUrl}`} 
                            alt="Media attachment" 
                            style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '4px' }} 
                            onError={(e) => {
                              e.target.src = m.mediaUrl.startsWith('http') ? m.mediaUrl : `${API_BASE.replace('/api', '')}${m.mediaUrl}`;
                            }}
                          />
                        ) : (
                          <a 
                            href={m.mediaUrl.startsWith('http') ? m.mediaUrl : `${API_BASE.replace('/api', '')}${m.mediaUrl}`} 
                            target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px', background: 'rgba(0,0,0,0.05)', borderRadius: '6px', textDecoration: 'none', color: '#0f172a', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}
                          >
                            <Paperclip size={16} /> Document / Media
                          </a>
                        )
                      )}
                      {m.body && <span style={{ fontSize: '14px', marginRight: '36px' }}>{m.body}</span>}
                      {isOut && m.sentByName && (
                        <span style={{ fontSize: '9px', fontStyle: 'italic', color: '#858585', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {m.sentByName === 'AI Assistant' && <span style={{fontSize:'12px'}} title="AI Auto-Reply">🤖</span>}
                          sent by {m.sentByName}
                        </span>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                        marginTop: '2px',
                        fontSize: '9px',
                        color: '#707070',
                        alignSelf: 'flex-end'
                      }}>
                        <span>{new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isOut && renderMsgStatus(m.status)}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Send bar */}
            <MediaPreview selectedMedia={selectedMedia} onClear={() => setSelectedMedia(null)} />
            <form
              onSubmit={handleSendMessage}
              style={{
                padding: '16px 24px',
                background: '#f0f2f5',
                display: 'flex',
                gap: '12px',
                alignItems: 'center'
              }}
            >
              <MediaSendButton onSelect={(file) => setSelectedMedia(file)} />
              <LibrarySendButton onClick={() => setShowMediaPicker(true)} />
              <button
                type="button"
                onClick={() => {
                  setNewChatInitialPhone(activeContact.phone);
                  setShowNewChatModal(true);
                }}
                style={{
                  background: '#e2e8f0',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  flexShrink: 0
                }}
                title="Send Template Message"
                onMouseEnter={e => e.currentTarget.style.background = '#cbd5e1'}
                onMouseLeave={e => e.currentTarget.style.background = '#e2e8f0'}
              >
                <FileText size={18} />
              </button>
              <QuickReplies onSelect={(text) => setNewMessageText(prev => prev ? `${prev} ${text}` : text)} />
              <input
                type="text"
                value={newMessageText}
                onChange={e => setNewMessageText(e.target.value)}
                placeholder={activeContact.assignedTo !== user.id ? "Accept chat to type a message..." : "Type a message..."}
                disabled={sendingMessage || activeContact.assignedTo !== user.id}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '24px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
                }}
              />
              <button
                type="submit"
                disabled={!newMessageText.trim() || sendingMessage || activeContact.assignedTo !== user.id}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(34,197,94,0.3)',
                  transition: 'opacity 0.2s',
                  opacity: (!newMessageText.trim() || sendingMessage || activeContact.assignedTo !== user.id) ? 0.6 : 1
                }}
              >
                <Send size={16} style={{ marginLeft: '2px' }} />
              </button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#64748b' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#22c55e20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#22c55e',
              marginBottom: '20px'
            }}>
              <MessageCircle size={48} />
            </div>
            <h3 style={{ margin: 0, color: '#334155' }}>WhatsApp Business Integration</h3>
            <p style={{ fontSize: '14px', marginTop: '6px', color: '#64748b', textAlign: 'center', maxWidth: '360px' }}>
              Select a conversation from the sidebar to start chatting.
            </p>
          </div>
        )}
      </div>

      {/* ── RIGHT PANEL: ACTIVITY AUDIT LOG ───────────────────────────────────── */}
      {activeContact && (
        <div style={{
          width: '280px',
          background: 'white',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <History size={18} color="#64748b" />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>Activity Audit Logs</h3>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {loadingLogs && logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#64748b' }}>Loading logs...</div>
            ) : logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', marginTop: '20px' }}>No activities recorded.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '10px',
                  bottom: '10px',
                  width: '2px',
                  background: '#f1f5f9',
                  zIndex: 0
                }} />

                {logs.map((log) => (
                  <div key={log._id} style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {renderLogActionIcon(log.action)}
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                          {log.action === 'chat_received' && 'Chat Initiated'}
                          {log.action === 'auto_assigned' && 'Auto-Assigned'}
                          {log.action === 'manually_accepted' && 'Chat Claimed'}
                          {log.action === 'reassigned' && 'Chat Transferred'}
                          {log.action === 'message_sent' && 'Agent Replied'}
                          {log.action === 'message_received' && 'Client Replied'}
                        </span>
                        <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                          {new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                        {log.note || `${log.performedByName || 'System'} modified chat state.`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      <NewChatModal 
        isOpen={showNewChatModal} 
        onClose={() => setShowNewChatModal(false)}
        initialPhone={newChatInitialPhone}
        onSuccess={(phone) => {
          setShowNewChatModal(false);
          setActiveTab('mine');
          setTimeout(() => {
            fetchContacts();
          }, 1000);
        }}
      />

      {showMediaPicker && (
        <MediaPickerModal
          isOpen={true}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(file) => {
            setSelectedMedia(file);
            setShowMediaPicker(false);
          }}
        />
      )}
    </div>
  );
};

const WhatsApp = () => {
  return (
    <WhatsAppSocketProvider>
      <MainLayout>
        <WhatsAppPageContent />
      </MainLayout>
    </WhatsAppSocketProvider>
  );
};

export default WhatsApp;
